import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, MapPin, Users } from 'lucide-react';
import DiamondNavigation from '@/components/3d/DiamondNavigation';
import CountdownTimer from '@/components/CountdownTimer';
import AbstractGuidelines from '@/components/AbstractGuidelines';
import { Button } from '@/components/ui/button';

const HomePage = () => {
  return (
    <div className="min-h-screen">
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_agos2026/artifacts/rmalf0ao_Outside%20Print%20%284%29.png"
            alt="Background pattern"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-12">
            <img
              src="https://customer-assets.emergentagent.com/job_agos2026/artifacts/zhdyk9m5_Outside%20Print%20%283%29.png"
              alt="AGOC 2026 Logo"
              className="w-full max-w-4xl mx-auto mb-12 scale-in-animation"
              data-testid="agoc-logo"
            />
            <p className="text-2xl md:text-4xl text-white font-semibold mb-8 slide-up-animation" data-testid="hero-tagline" style={{ animationDelay: '0.3s' }}>
              Vision • Innovation • Excellence
            </p>
            
            <div className="glass-card inline-block px-12 py-8 mb-8 scale-in-animation" style={{ animationDelay: '0.5s' }}>
              <p className="text-slate-300 text-lg mb-4 uppercase tracking-wider">Organized by</p>
              <img
                src="https://customer-assets.emergentagent.com/job_agos2026/artifacts/2gn5mq4i_logo.png"
                alt="All Gujarat Ophthalmic Society"
                className="w-56 mx-auto mb-6 hover:scale-110 transition-transform duration-300"
                data-testid="agos-logo"
              />
              <p className="text-slate-300 text-lg mb-4 uppercase tracking-wider">Hosted by</p>
              <img
                src="https://customer-assets.emergentagent.com/job_agos2026/artifacts/ize0w94o_2d4c14ed-b454-4902-9f1c-2f0c7daaffd7-removebg-preview.png"
                alt="South Gujarat Academy of Ophthalmology"
                className="w-56 mx-auto hover:scale-110 transition-transform duration-300"
                data-testid="sgao-logo"
              />
            </div>
            
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-lg md:text-xl text-slate-200 mb-8 slide-left-animation" style={{ animationDelay: '0.7s' }}>
              <div className="flex items-center gap-3 glass-card px-6 py-3 neon-glow hover:scale-105 transition-transform duration-300" data-testid="event-date">
                <Calendar className="h-6 w-6 text-[#FF6F4A]" />
                <span className="font-medium">October 24-25, 2026</span>
              </div>
              <div className="flex items-center gap-3 glass-card px-6 py-3 hover:scale-105 transition-transform duration-300" data-testid="event-location">
                <MapPin className="h-6 w-6 text-[#FFB6C1]" />
                <span className="font-medium">Le Meridien, Surat</span>
              </div>
            </div>
            <div className="glass-card inline-block px-8 py-4 mb-8 slide-right-animation neon-glow" style={{ animationDelay: '0.9s' }}>
              <p className="text-[#FFB6C1] text-lg md:text-xl font-semibold shimmer-effect">
                Join 2000+ Ophthalmologists | 50+ Expert Speakers | 2 Days of Excellence
              </p>
            </div>
          </div>

          <div className="mb-16">
            <CountdownTimer />
          </div>

          <DiamondNavigation />

          <div className="flex flex-wrap items-center justify-center gap-4 mt-12 slide-up-animation" style={{ animationDelay: '1.1s' }}>
            <Link to="/registration">
              <Button
                size="lg"
                className="bg-[#FF6F4A] hover:bg-[#FF6F4A]/90 text-white font-bold px-10 py-7 text-xl neon-glow transform hover:scale-110 transition-all duration-300"
                data-testid="register-now-button"
              >
                🎯 Secure Your Spot Now
                <ArrowRight className="ml-3 h-6 w-6 inline-block animate-bounce" />
              </Button>
            </Link>
            <Link to="/abstracts">
              <Button
                size="lg"
                variant="outline"
                className="glass-card text-white border-2 border-[#FFB6C1] hover:bg-[#FFB6C1]/20 px-10 py-7 text-xl font-semibold transform hover:scale-110 transition-all duration-300"
                data-testid="submit-abstract-button"
              >
                📄 Submit Your Research
              </Button>
            </Link>
          </div>

          <div className="mt-12 glass-card p-6 max-w-2xl mx-auto neon-glow scale-in-animation" style={{ animationDelay: '1.3s' }}>
            <p className="text-center text-[#FF6F4A] font-bold text-lg animate-pulse">
              Register Now for AGOC 2026! Limited Seats Available
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="highlights-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl md:text-5xl font-semibold text-center mb-12 slide-up-animation" data-testid="highlights-title">
            Conference <span className="gradient-text">Highlights</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="glass-card p-8 hover:scale-110 transition-transform duration-300 slide-left-animation" data-testid="highlight-attendees" style={{ animationDelay: '0.2s' }}>
              <div className="inline-block p-4 glass-card neon-glow mb-4 float-animation">
                <Users className="h-8 w-8 text-[#FF6F4A]" />
              </div>
              <h3 className="text-2xl font-bold mb-2">2000+ Attendees</h3>
              <p className="text-slate-300 leading-relaxed">
                Join ophthalmologists, residents, and fellows from across India
              </p>
            </div>

            <div className="glass-card p-8 hover:scale-110 transition-transform duration-300 slide-up-animation" data-testid="highlight-speakers" style={{ animationDelay: '0.4s' }}>
              <div className="inline-block p-4 glass-card neon-glow mb-4 float-animation" style={{ animationDelay: '1s' }}>
                <Users className="h-8 w-8 text-[#FFB6C1]" />
              </div>
              <h3 className="text-2xl font-bold mb-2">50+ Speakers</h3>
              <p className="text-slate-300 leading-relaxed">
                Learn from leading experts in various ophthalmology subspecialties
              </p>
            </div>

            <div className="glass-card p-8 hover:scale-110 transition-transform duration-300 slide-right-animation" data-testid="highlight-sessions" style={{ animationDelay: '0.6s' }}>
              <div className="inline-block p-4 glass-card neon-glow mb-4 float-animation" style={{ animationDelay: '2s' }}>
                <Calendar className="h-8 w-8 text-[#FF6F4A]" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Multiple Sessions</h3>
              <p className="text-slate-300 leading-relaxed">
                Two days packed with lectures, workshops, and networking events
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="abstract-guidelines-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-semibold mb-4">
              Paper &amp; E-Poster <span className="gradient-text">Guidelines</span>
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Planning to present at AGOC 2026? Read the guidelines carefully before submitting your Free Paper or E-Poster.
            </p>
          </div>
          <AbstractGuidelines showSubmission={true} />
          <div className="text-center mt-10">
            <Link to="/abstracts">
              <Button
                size="lg"
                className="bg-[#FF6F4A] hover:bg-[#FF6F4A]/90 text-white font-bold px-10 py-6 text-lg neon-glow transform hover:scale-105 transition-all duration-300"
                data-testid="home-submit-abstract-button"
              >
                📄 Check Abstract Status &amp; Guidelines
                <ArrowRight className="ml-3 h-5 w-5 inline-block" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 glass-card" data-testid="about-preview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-5xl font-semibold mb-6">
                About <span className="gradient-text">AGOC 2026</span>
              </h2>
              <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-6">
                The All Gujarat Ophthalmic Society Annual Conference (AGOC) is the premier gathering
                of eye care professionals in Gujarat. AGOC 2026 brings together the brightest
                minds in ophthalmology to share knowledge, innovations, and best practices.
              </p>
              <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-8">
                Organized by <strong className="text-white">All Gujarat Ophthalmic Society (AGOS)</strong> and
                hosted by <strong className="text-white">South Gujarat Academy of Ophthalmology (SGAO)</strong>,
                this year's theme focuses on cutting-edge advancements in retinal care,
                cataract surgery, glaucoma management, and comprehensive ophthalmology.
              </p>
              <Link to="/about">
                <Button
                  className="bg-[#FF6F4A] hover:bg-[#FF6F4A]/90 text-white font-semibold"
                  data-testid="learn-more-button"
                >
                  Learn More
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1764336222789-28fd1846c6e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxvcGh0aGFsbW9sb2d5JTIwcmV0aW5hJTIwZXllJTIwYWJzdHJhY3QlMjBtZWRpY2FsJTIwZnV0dXJpc3RpY3xlbnwwfHx8fDE3NzM5NDM0Mzl8MA&ixlib=rb-4.1.0&q=85"
                alt="Ophthalmology"
                className="rounded-2xl glass-card w-full h-auto"
                data-testid="about-image"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;